import { supabase } from '../lib/supabase';
import { WhatsAppService } from '../lib/whatsapp';class NotificationService {
  /**
   * Create a notification
   */
  async createNotification({
    userId,
    organizationId,
    centerId = null,
    type,
    title,
    message,
    relatedId = null,
    relatedType = null,
    actionUrl = null,
    priority = 'medium',
    scheduledFor = null
  }) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          organization_id: organizationId,
          center_id: centerId,
          type,
          title,
          message,
          related_id: relatedId,
          related_type: relatedType,
          action_url: actionUrl,
          priority,
          scheduled_for: scheduledFor,
          is_read: false,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Get notifications for a user
   */
  async getUserNotifications(userId, { limit = 50, unreadOnly = false } = {}) {
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (unreadOnly) {
        query = query.eq('is_read', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Alias for getUserNotifications
   */
  async getNotifications(userId, options = {}) {
    return this.getUserNotifications(userId, options);
  }

  /**
   * Create multiple notifications at once
   */
  async createBulkNotifications(notifications) {
    try {
      const notificationData = notifications.map(notif => ({
        user_id: notif.userId,
        organization_id: notif.organizationId,
        center_id: notif.centerId || null,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        related_id: notif.relatedId || null,
        related_type: notif.relatedType || null,
        action_url: notif.actionUrl || null,
        priority: notif.priority || 'medium',
        scheduled_for: notif.scheduledFor || null,
        is_read: false,
        created_at: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('notifications')
        .insert(notificationData)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating bulk notifications:', error);
      throw error;
    }
  }

  /**
   * Send WhatsApp notification if user has opted in
   */
  async sendWhatsAppNotification(userId, message, templateName = null, templateParams = []) {
    try {
      // Check if user has WhatsApp enabled
      const { data: user, error: userError } = await supabase
        .from('profiles')
        .select('whatsapp_number, whatsapp_opt_in')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      if (!user.whatsapp_opt_in || !user.whatsapp_number) {
        return { success: false, message: 'User not opted in for WhatsApp' };
      }

      const whatsappService = new WhatsAppService();
      
      let result;
      if (templateName) {
         result = await whatsappService.sendTemplate(user.whatsapp_number, templateName, 'en_US', templateParams);
      } else {
         result = await whatsappService.sendTextMessage(user.whatsapp_number, message);
      }

      return { 
        success: result.success, 
        message: result.success ? 'WhatsApp notification sent' : 'Failed to send WhatsApp notification',
        result,
        whatsapp_number: user.whatsapp_number
      };
    } catch (error) {
      console.error('Error sending WhatsApp notification:', error);
      return { success: false, error };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error marking all as read:', error);
      return { success: false, error };
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(userId) {
    try {
      const { count, data, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      // Return count if available, otherwise count data array length
      return count !== undefined && count !== null ? count : (data ? data.length : 0);
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  }

  /**
   * Send payment reminder
   */
  async sendPaymentReminder(payment, student, parent) {
    const dueDate = new Date(payment.due_date);
    const today = new Date();
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    let message = '';
    let priority = 'medium';

    if (daysUntilDue < 0) {
      message = `Payment for ${student.first_name} ${student.last_name} is ${Math.abs(daysUntilDue)} days overdue. Amount: ${payment.amount}`;
      priority = 'high';
    } else if (daysUntilDue === 0) {
      message = `Payment for ${student.first_name} ${student.last_name} is due today. Amount: ${payment.amount}`;
      priority = 'high';
    } else if (daysUntilDue <= 3) {
      message = `Payment for ${student.first_name} ${student.last_name} is due in ${daysUntilDue} days. Amount: ${payment.amount}`;
      priority = 'medium';
    } else if (daysUntilDue <= 7) {
      message = `Reminder: Payment for ${student.first_name} ${student.last_name} is due in ${daysUntilDue} days. Amount: ${payment.amount}`;
      priority = 'low';
    }

    if (message) {
      const notification = await this.createNotification({
        userId: parent.user_id,
        organizationId: payment.organization_id,
        centerId: payment.center_id,
        type: 'payment_reminder',
        title: daysUntilDue < 0 ? 'Overdue Payment' : 'Payment Reminder',
        message,
        relatedId: payment.id,
        relatedType: 'payment',
        actionUrl: '/app/payments',
        priority
      });

      // Dispatch WhatsApp message for high priority or due today
      if (priority === 'high' || daysUntilDue === 0) {
        const monthYear = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
        const parentName = parent.first_name || 'Parent';
        
        await this.sendWhatsAppNotification(parent.user_id, message, 'payment_reminder_basic', [
          { type: 'text', text: parentName },
          { type: 'text', text: monthYear },
          { type: 'text', text: payment.amount.toString() },
          { type: 'text', text: new Date(payment.due_date).toLocaleDateString() }
        ]);
      }

      return notification;
    }

    return { success: false, message: 'No reminder needed' };
  }

  /**
   * Send class reminder
   */
  async sendClassReminder(classData, students) {
    const classTime = new Date(classData.start_time);
    const now = new Date();
    const hoursUntilClass = Math.ceil((classTime - now) / (1000 * 60 * 60));

    if (hoursUntilClass <= 24 && hoursUntilClass > 0) {
      const message = hoursUntilClass <= 2
        ? `Your class "${classData.name}" starts in ${hoursUntilClass} hour${hoursUntilClass !== 1 ? 's' : ''}`
        : `Reminder: You have class "${classData.name}" tomorrow at ${new Date(classData.start_time).toLocaleTimeString()}`;

      const notifications = await Promise.all(students.map(async student => {
        const priority = hoursUntilClass <= 2 ? 'high' : 'medium';
        const notification = await this.createNotification({
          userId: student.parent_user_id || student.user_id,
          organizationId: classData.organization_id,
          centerId: classData.center_id,
          type: 'class_reminder',
          title: priority === 'high' ? 'Class Starting Soon' : 'Upcoming Class',
          message,
          relatedId: classData.id,
          relatedType: 'class',
          actionUrl: '/app/classes',
          priority
        });

        if (priority === 'high') {
          await this.sendWhatsAppNotification(
            student.parent_user_id || student.user_id, 
            message, 
            'class_reminder_basic', 
            [
              { type: 'text', text: student.first_name },
              { type: 'text', text: classData.name },
              { type: 'text', text: new Date(classData.start_time).toLocaleTimeString() }
            ]
          );
        }

        return notification;
      }));

      return notifications;
    }

    return { success: false, message: 'No reminder needed' };
  }

  /**
   * Send low attendance alert
   */
  async sendLowAttendanceAlert(student, attendanceRate, organizationOwner) {
    if (attendanceRate < 60) {
      const message = `${student.first_name} ${student.last_name}'s attendance has dropped to ${attendanceRate}%. Please follow up with the parent.`;

      const notification = await this.createNotification({
        userId: organizationOwner.user_id,
        organizationId: student.organization_id,
        centerId: student.center_id,
        type: 'attendance_alert',
        title: 'Low Attendance Alert',
        message,
        relatedId: student.id,
        relatedType: 'student',
        actionUrl: `/app/students/${student.id}`,
        priority: 'high'
      });

      // Notify the owner contextually (assuming owner is the user)
      await this.sendWhatsAppNotification(organizationOwner.user_id, message);

      return notification;
    }

    return { success: false, message: 'Attendance acceptable' };
  }

  /**
   * Send belt test reminder
   */
  async sendBeltTestReminder(student, testDate, parent) {
    const daysUntilTest = Math.ceil((new Date(testDate) - new Date()) / (1000 * 60 * 60 * 24));

    if (daysUntilTest <= 7 && daysUntilTest > 0) {
      const message = `${student.first_name} ${student.last_name}'s belt test is scheduled in ${daysUntilTest} days on ${new Date(testDate).toLocaleDateString()}.`;

      const notification = await this.createNotification({
        userId: parent.user_id,
        organizationId: student.organization_id,
        centerId: student.center_id,
        type: 'belt_test_reminder',
        title: 'Upcoming Belt Test',
        message,
        relatedId: student.id,
        relatedType: 'student',
        actionUrl: '/app/student-progress',
        priority: daysUntilTest <= 3 ? 'high' : 'medium'
      });

      if (daysUntilTest <= 3) {
         await this.sendWhatsAppNotification(parent.user_id, message);
      }

      return notification;
    }

    return { success: false, message: 'No reminder needed' };
  }

  /**
   * Send new student welcome notification
   */
  async sendWelcomeNotification(student, parent) {
    const message = `Welcome to our karate club! We're excited to have ${student.first_name} join us. Your first class details will be shared soon.`;

    const notification = await this.createNotification({
      userId: parent.user_id,
      organizationId: student.organization_id,
      centerId: student.center_id,
      type: 'welcome',
      title: 'Welcome to Kounga Karate!',
      message,
      relatedId: student.id,
      relatedType: 'student',
      actionUrl: '/app/classes',
      priority: 'medium'
    });

    await this.sendWhatsAppNotification(parent.user_id, message, 'welcome_new_student', [
      { type: 'text', text: student.first_name }
    ]);

    return notification;
  }
}

export default new NotificationService();
